import { useState, useContext, useEffect } from "react";
import axiosClient from "../api/axiosClient";
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Card,
  CardMedia,
  Chip,
  Alert
} from "@mui/material";
import {
  Link as LinkIcon,
  EmojiEvents,
  CalendarToday
} from "@mui/icons-material";

export default function Home() {
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchToday = async () => {
      try {
        setLoading(true);
        // Uncomment when your API is ready
        // const res = await axiosClient.get("/history/today-history");
        // setToday(res.data);
        
        // Simulate loading for demo
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      } catch (err) {
        setError("Failed to load today's special.");
        setLoading(false);
      }
    };

    fetchToday();
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Welcome Section */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          mb: 4,
          // background: "linear-gradient(135deg, #3E1E68 0%, #5D2F77 100%)",
          background:"#2a1c32ff",
          color: "white",
          borderRadius: 3,
          textAlign: "center"
        }}
      >
        <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
          📝 Welcome to Notes App
        </Typography>
        <Typography variant="h5" sx={{ opacity: 0.9, mb: 3 }}>
          Organize your thoughts, ideas, and important information in one place
        </Typography>
        
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
          <Chip 
            label="Easy to Use" 
            sx={{ backgroundColor: "rgba(255,255,255,0.2)", color: "white" }} 
          />
          <Chip 
            label="Secure" 
            sx={{ backgroundColor: "rgba(255,255,255,0.2)", color: "white" }} 
          />
          <Chip 
            label="Accessible Everywhere" 
            sx={{ backgroundColor: "rgba(255,255,255,0.2)", color: "white" }} 
          />
        </Box>
      </Paper>

      {/* Today's Special Section */}
      {!today && !loading ? (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            // background: "linear-gradient(135deg, #5D2F77 0%, #3E1E68 100%)",
            background:"#2a1c32ff",
            color: "white",
            borderRadius: 3
          }}
        >
          <EmojiEvents sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
          <Typography variant="h4" component="h2" gutterBottom>
            Welcome to Our Notes App
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
            Start creating your first note to get organized!
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="/add-note"
            sx={{
              backgroundColor: "white",
              color: "#3E1E68",
              fontWeight: "bold",
              px: 4,
              "&:hover": {
                backgroundColor: "#f5f5f5",
                transform: "translateY(-2px)",
                boxShadow: 3
              }
            }}
          >
            Create Your First Note
          </Button>
        </Paper>
      ) : (
        <Paper
          elevation={3}
          sx={{
            p: 4,
            background: "linear-gradient(135deg, #5D2F77 0%, #3E1E68 100%)",
            color: "white",
            borderRadius: 3
          }}
        >
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <CalendarToday sx={{ mr: 2, fontSize: 32 }} />
            <Box>
              <Chip
                label="Featured Today"
                sx={{
                  backgroundColor: "rgba(255,255,255,0.2)",
                  color: "white",
                  mb: 1
                }}
              />
              <Typography variant="h3" component="h1" fontWeight="bold">
                Today's Special
              </Typography>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="h6">Loading today's special...</Typography>
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          ) : (
            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 4 }}>
              {/* Image */}
              {today.image_url && (
                <Box sx={{ flex: 1 }}>
                  <Card sx={{ borderRadius: 2, overflow: "hidden" }}>
                    <CardMedia
                      component="img"
                      image={today.image_url}
                      alt={today.title}
                      sx={{
                        width: "100%",
                        maxHeight: 300,
                        objectFit: "cover"
                      }}
                    />
                  </Card>
                </Box>
              )}

              {/* Content */}
              <Box sx={{ flex: 2 }}>
                <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
                  {today.title}
                </Typography>
                
                <Paper
                  sx={{
                    p: 3,
                    backgroundColor: "rgba(255,255,255,0.1)",
                    borderRadius: 2,
                    mb: 3
                  }}
                >
                  <Typography variant="body1" sx={{ lineHeight: 1.8, fontSize: "1.1rem" }}>
                    {today.description}
                  </Typography>
                </Paper>

                {today.reference_link && (
                  <Button
                    component="a"
                    href={today.reference_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<LinkIcon />}
                    variant="outlined"
                    size="large"
                    sx={{
                      color: "white",
                      borderColor: "white",
                      px: 4,
                      "&:hover": {
                        borderColor: "white",
                        backgroundColor: "rgba(255,255,255,0.1)"
                      }
                    }}
                  >
                    Learn More
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      )}
    </Container>
  );
}